/*
  Header group behaviour.
  <announcement-bar>: steps through announcements with previous/next buttons.
  <site-header>: navigation panels, the menu drawer, the search dialog, the
  cart link and the --header-height token used for scroll offsets.
*/
(function () {
  if (!customElements.get('announcement-bar')) {
    class AnnouncementBar extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-announcement-item]'));
        if (this.items.length < 2) return;

        this.querySelectorAll('[data-announcement-nav]').forEach((button) => {
          button.hidden = false;
          button.addEventListener('click', () => this.show(this.index + Number(button.dataset.announcementNav)));
        });

        /* In the theme editor, show the announcement that is being edited. */
        this.addEventListener('shopify:block:select', (event) => {
          const position = this.items.indexOf(event.target);
          if (position >= 0) this.show(position);
        });

        this.show(0);
      }

      show(position) {
        const count = this.items.length;
        this.index = (position + count) % count;
        this.items.forEach((item, itemPosition) => {
          item.hidden = itemPosition !== this.index;
        });
      }
    }

    customElements.define('announcement-bar', AnnouncementBar);
  }

  if (!customElements.get('site-header')) {
    class SiteHeader extends HTMLElement {
      connectedCallback() {
        this.backdrop = this.querySelector('[data-header-backdrop]');
        this.drawer = this.querySelector('[data-header-drawer]');
        this.activeOverlay = null;
        this.activeOpener = null;

        /* The drawer is plain page content without JavaScript, a dialog with it. */
        if (this.drawer) {
          this.drawer.setAttribute('role', 'dialog');
          this.drawer.setAttribute('aria-modal', 'true');
        }

        this.onClick = this.onClick.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onBreakpoint = this.onBreakpoint.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);

        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.desktopQuery.addEventListener('change', this.onBreakpoint);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updateHeight());
          this.resizeObserver.observe(this);
        }
        this.updateHeight();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onBreakpoint);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.activeOverlay) {
          window.Theme.releaseFocus(false);
          window.Theme.unlockScroll();
          this.activeOverlay = null;
        }
      }

      /* A sticky header covers content, so anchors and sticky elements offset by its height. */
      updateHeight() {
        const height = this.hasAttribute('data-sticky') ? this.offsetHeight : 0;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }

      onClick(event) {
        const target = event.target;
        const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

        const toggle = target.closest('[data-nav-toggle]');
        if (toggle) {
          this.togglePanel(toggle);
          return;
        }

        const opener = target.closest('[data-overlay-open]');
        if (opener) {
          if (modified && opener.tagName === 'A') return;
          event.preventDefault();
          this.openOverlay(opener);
          return;
        }

        if (target.closest('[data-overlay-close]')) {
          this.closeOverlay();
          return;
        }

        const cartLink = target.closest('[data-cart-link]');
        if (cartLink && cartLink.hasAttribute('data-cart-drawer') && !modified) {
          event.preventDefault();
          window.Theme.emit('cart:open');
        }
      }

      /* Navigation panels */

      togglePanel(toggle) {
        const item = toggle.closest('[data-nav-item]');
        const willOpen = !item.classList.contains('is-open');
        this.closePanels();
        if (!willOpen) return;
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }

      closePanels() {
        this.querySelectorAll('[data-nav-item].is-open').forEach((item) => {
          item.classList.remove('is-open');
          item.querySelector('[data-nav-toggle]').setAttribute('aria-expanded', 'false');
        });
      }

      onFocusOut(event) {
        const item = event.target.closest('[data-nav-item]');
        if (item && event.relatedTarget && !item.contains(event.relatedTarget)) this.closePanels();
      }

      onDocumentClick(event) {
        if (!event.target.closest('[data-nav-item]')) this.closePanels();
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape') return;

        const openItem = this.querySelector('[data-nav-item].is-open');
        if (openItem) {
          this.closePanels();
          openItem.querySelector('[data-nav-toggle]').focus();
          return;
        }

        if (this.activeOverlay) this.closeOverlay();
      }

      /* Menu drawer and search dialog */

      openOverlay(opener) {
        const overlay = document.getElementById(opener.dataset.overlayOpen);
        if (!overlay) return;
        if (this.activeOverlay) this.closeOverlay(false);
        this.closePanels();

        this.activeOverlay = overlay;
        this.activeOpener = opener;
        overlay.classList.add('is-open');
        if (opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'true');
        if (this.backdrop) this.backdrop.hidden = false;

        window.Theme.lockScroll();
        window.Theme.trapFocus(overlay, overlay.querySelector('[data-autofocus], input[type="search"]'));
      }

      closeOverlay(restoreFocus = true) {
        const overlay = this.activeOverlay;
        const opener = this.activeOpener;
        if (!overlay) return;

        this.activeOverlay = null;
        this.activeOpener = null;
        overlay.classList.remove('is-open');
        if (opener && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'false');
        if (this.backdrop) this.backdrop.hidden = true;

        window.Theme.releaseFocus(false);
        window.Theme.unlockScroll();
        if (restoreFocus && opener) opener.focus();
      }

      onBreakpoint(event) {
        if (event.matches && this.activeOverlay && this.activeOverlay === this.drawer) this.closeOverlay(false);
      }
    }

    customElements.define('site-header', SiteHeader);
  }
})();
